import { rampSurfaceZ } from "../player/slope";
import type { FigureId, TerrainSampler } from "../world/types";
import { TILE_SHAPE_RAMP, TILE_SHAPE_STAIR_UP } from "../world/types";

/*
 * Ambient bodies: stand, amble a few tiles, stand again.
 *
 * # What this is for, since item 6
 *
 * **The crowd layer, and it is empty at this scale** (Phase 7, item 6's rider 1,
 * ruled 2026-08-28 — *one owner per body*). Until that ruling this loop stood up
 * a settlement's **named roster** wherever the simulation had not promoted it,
 * which meant the same ten people were drawn two ways by two systems and a
 * promotion swapped one for the other under a standing player. A named figure is
 * drawn from the simulation's presence list now or is not drawn at all, so
 * nothing in `witness/witness.ts` adds a figure to this class any more.
 *
 * What it keeps is its future, and the future is the other population:
 * `worldgen-direction.md` §1 rules that a city's named roster is real and
 * everybody else is *"crowds, visuals only — client-side presence driven by
 * sim-side numbers, seen and never simulated"*. That is **unnamed** bodies, a
 * count of `population − named`, no `FigureId`s and nothing to talk to — a
 * Phase 8 job, when a city has thousands in it and two hundred of them are
 * named. At Phase 7's scale a town's whole population *is* its named roster, so
 * the count is zero and this class is instantiated, stepped, cleared and drawn
 * with nobody in it.
 *
 * It is kept rather than deleted for that reason and for one more: everything
 * below is the behaviour that layer will want — a loiter radius, a dwell, a
 * gathering with a direction to face — and it is checked
 * (`dev/selfcheck/crowd.ts`). The `figure` on {@link AmbientBody} is what will
 * have to go when the unnamed bodies arrive; it is left here because the class
 * is unchanged, not because a crowd body has an identity.
 *
 * # What it is, mechanically
 *
 * This is DF's terminal fallback and nothing more. `choose_behavior` runs down
 * moods, biology, committed jobs, activities, the emotion gate, squad orders and
 * work, and when every one of those declines it reaches the last line of the
 * function — `wander_within(u, u->idle_area, u->idle_area_threshold)`
 * (`docs/research/df-systems-map/systems/units-and-ai.md` §6.3). An anchor point
 * and a loiter radius is the whole of it, and the loiter radius is DF's own:
 * `unit.idle_area_threshold` is `stationdist`, initialised to **3**
 * (`pathfinding.md` §2.3's field table). The dwell between walks is DF's
 * activity `timer` countdown (units §6.2's participants/where/what/countdown
 * shape) with a duration we picked, because the study records the field and not
 * its value.
 *
 * Three things this deliberately is not, and each has a reason in the plan:
 *
 * - **Not pathfinding.** A body walks a straight line to a point inside its own
 *   loiter circle and the terrain under it is sampled, not collided with. Phase
 *   3's cut list has NPC schedules in it and the exit criterion has nothing to
 *   do with where anybody walked.
 * - **Not simulation.** None of this hashes and the sim never learns an NPC's
 *   position (plan decision 8) — embodiment is client-side presentation, and
 *   identity plus knowledge is the only part that crosses the seam. The
 *   randomness below is therefore *client* randomness: seeded per figure so a
 *   screenshot repeats, and never an input to anything the world remembers.
 * - **Not collision.** "Terrain-aware enough not to float" is the whole bar:
 *   {@link surfaceZ} puts feet on the ground, including on a ramp, and a body
 *   that ambles into a wall walks up the wall's ramp or stands against it.
 *   Nothing here can push the player, and nothing here is pushed.
 */

/**
 * How far from its anchor an idle body will drift, in tiles.
 *
 * DF's `stationdist`, initialised to 3 (`pathfinding.md` §2.3). Transcribed
 * rather than tuned: it is one of the few numbers in this area the study
 * actually records a value for.
 */
export const LOITER_RADIUS_TILES = 3;

/**
 * How far a body drifts while it is standing at a gathering, in tiles.
 *
 * PROVISIONAL, and small on purpose: a crowd that came to hear something stands
 * still, and the shuffle is the difference between "standing" and "frozen"
 * rather than a walk. It is also load-bearing arithmetic — the caller sizes the
 * ring so that even a body drifting inwards by this much stays outside the
 * clearance the market board needs to remain readable (`witness/witness.ts`), so
 * a gathering can never take the press meant for the thing it gathered about.
 */
export const GATHERED_LOITER_TILES = 0.6;

/**
 * How long a body stands still between walks, in seconds.
 *
 * PROVISIONAL. Units §6.2's leisure events all carry a `timer` countdown and the
 * study records no value for any of them, so this is a feel number chosen to
 * read as "people milling about" rather than "people commuting": long enough
 * that a settlement looks inhabited when the player stands still, short enough
 * that nobody looks frozen.
 */
const DWELL_SECONDS_MIN = 2.5;
const DWELL_SECONDS_MAX = 7;

/**
 * What a town in ruin does to the milling about: how much longer a body stands
 * still, and how much less far it drifts. PROVISIONAL.
 *
 * **Phase 5 decision 11's "quieted market", in the only currency this client
 * has.** There is no sound in this tree and the cut list keeps it that way, so
 * "quieted" cannot be a volume — what it can be is the *movement* that reads as
 * a market: people crossing a square, changing their minds, going somewhere.
 * Two and a half times the dwell and under half the drift turns that into
 * people standing about in the wreckage, which is the same fact drawn in the
 * one register the client owns.
 *
 * Both are multipliers rather than second constants, so the ordinary numbers
 * above stay the single source of what a crowd is and this is visibly a
 * *modifier* on them. Nothing about it reaches the simulation: a ruin is a
 * projection over the log and how a client draws one cannot be seen by a replay
 * (`world/types.ts`, `RuinView`).
 */
const SUBDUED_DWELL_FACTOR = 2.5;
const SUBDUED_LOITER_FACTOR = 0.45;

/**
 * How fast a body ambles, in tiles per second.
 *
 * PROVISIONAL, and a third of the player's `WALK_SPEED` on purpose: an idler
 * moving at walking pace reads as someone going somewhere, which is the one
 * thing this loop must not imply.
 */
const AMBLE_SPEED = 1.4;

/** How fast a body turns towards where it is going, in radians per second. PROVISIONAL. */
const TURN_RATE = 4;

/** How close counts as arrived, in tiles. Below this the body stops and dwells. */
const ARRIVED_TILES = 0.2;

/**
 * How far above and below the column's nominal floor the ground is looked for.
 *
 * Two up because a terrace or a ramp cap can sit above the elevation field's
 * answer; eight down because that is as deep as a dug pit under somebody's feet
 * is worth following before giving up and standing them on the ground the
 * worldgen field says is there.
 */
const PROBE_UP = 2;
const PROBE_DOWN = 8;

/** A body as everything outside this module sees it: a pose, and who it belongs to. */
export interface AmbientBody {
  readonly figure: FigureId;
  readonly name: string;

  /** Feet centre, in world tiles. */
  readonly x: number;
  readonly y: number;
  readonly z: number;

  /** Which way it is turned, in radians. Yaw 0 faces +y — `player/controller.ts`'s convention. */
  readonly facing: number;

  /** `true` while it is walking, `false` while it dwells. Drives nothing yet; the HUD may. */
  readonly walking: boolean;
}

/** The same body, from the inside, where it has somewhere to be. */
interface Wanderer {
  readonly figure: FigureId;
  readonly name: string;

  /**
   * Where this body loiters when nothing is happening — the spot
   * `witness/witness.ts` laid out around the settlement, and the spot it goes
   * back to when a gathering breaks up ({@link AmbientCrowd.release}).
   */
  readonly homeX: number;
  readonly homeY: number;

  /** Where it loiters *now*: the home spot, or a place on the ring of a gathering. */
  anchorX: number;
  anchorY: number;

  x: number;
  y: number;
  z: number;
  facing: number;
  walking: boolean;

  /** Where it is heading, in world tiles. Inside the loiter circle by construction. */
  targetX: number;
  targetY: number;

  /** Seconds left standing still. Zero means "walk". */
  dwell: number;

  /** The tile the footing was last probed for, so the probe is not run every tick. */
  probedX: number;
  probedY: number;

  /** This body's own stream of client randomness. */
  readonly random: () => number;
}

/**
 * The settlement's idle crowd — **empty at this scale**, see the module note.
 *
 * Owns every ambient body and the one thing they all share — a
 * {@link TerrainSampler} to stand on. Stepped from the host's fixed step, so the
 * motion is tick-rate and the renderer interpolates nothing: a body ambling at
 * 1.4 tiles a second moves half a millimetre between two frames, and the
 * machinery that makes the player smooth is not worth spending on that.
 */
export class AmbientCrowd {
  private readonly terrain: TerrainSampler;
  private readonly wanderers: Wanderer[] = [];

  /**
   * Where the crowd has been called to, or `null` when everybody is at home.
   *
   * Phase 4 decision 5's converging crowd, and the whole of the ambient loop's
   * part in it: a gathering is the same loiter behaviour with the anchors moved
   * and a direction to face. There is no second state machine and no
   * pathfinding — DF's fallback is still `wander_within(u, u->idle_area, …)`
   * (units §6.3) and this moves `idle_area`, which is exactly what a meeting
   * location is.
   */
  private focus: { x: number; y: number } | null = null;

  /**
   * Whether this crowd is standing in a town that is still in ruin.
   *
   * Phase 5 decision 11's third item ({@link SUBDUED_DWELL_FACTOR}). A flag on
   * the crowd rather than on each body, because it is a fact about the *place*
   * and the bodies are re-anchored whole whenever the place changes.
   *
   * **Nothing sets it today.** A ruined town's thinned, quietened street was the
   * ambient loop's answer over a *named* roster and the simulation's roster is
   * the honest one now (item 6, rider 1) — who is alive in a sacked town is a
   * fact the log holds. It stays here because it is the crowd layer's own
   * behaviour and the unnamed population of a sacked city will want it.
   */
  private subdued = false;

  constructor(terrain: TerrainSampler) {
    this.terrain = terrain;
  }

  /** Every body, in the order they were added. */
  get bodies(): readonly AmbientBody[] {
    return this.wanderers;
  }

  get size(): number {
    return this.wanderers.length;
  }

  /**
   * Anchors a figure at a point and lets them loiter around it.
   *
   * The anchor is the client's choice and never the simulation's — the sim has
   * no opinion about where in a settlement anybody stands (plan decision 8) —
   * and it is horizontal only: how high the body stands is {@link surfaceZ}'s
   * answer and changes as they walk.
   */
  add(figure: FigureId, name: string, anchorX: number, anchorY: number): void {
    // Seeded from the figure's own id, so the same roster produces the same
    // milling about on every reload and a screenshot can be taken twice.
    const random = seededRandom(figure);
    const wanderer: Wanderer = {
      figure,
      name,
      homeX: anchorX,
      homeY: anchorY,
      anchorX,
      anchorY,
      x: anchorX,
      y: anchorY,
      z: surfaceZ(this.terrain, anchorX, anchorY),
      // Facing outwards from the anchor by an arbitrary amount, rather than
      // every body in the settlement facing north on the first frame.
      facing: random() * Math.PI * 2 - Math.PI,
      walking: false,
      targetX: anchorX,
      targetY: anchorY,
      dwell: random() * DWELL_SECONDS_MAX,
      probedX: Number.NaN,
      probedY: Number.NaN,
      random,
    };

    this.wanderers.push(wanderer);
  }

  /** Drops every body. The registry drawing them is cleared separately. */
  clear(): void {
    this.wanderers.length = 0;
    this.focus = null;
    // The mood belongs to the town whose people these were, so it goes with
    // them: the next `setSubdued` is the next town's answer.
    this.subdued = false;
  }

  /** Whether the crowd has been called somewhere. */
  get gathered(): boolean {
    return this.focus !== null;
  }

  /**
   * Calls every body in towards a point, to stand at `radius` from it.
   *
   * Each one comes in **along its own bearing from the focus** rather than to an
   * assigned place on the ring: nobody crosses anybody, the crowd keeps the
   * shape it had, and the whole movement reads as a town turning to face the
   * same thing. The alternative — dealing out positions around the circle — has
   * half the settlement walking through the other half to reach them.
   *
   * The radius is the caller's, and the caller has a reason for it: the market
   * board is what the crowd converges on and it is also what the player has to
   * be able to walk up to and read, so `witness/witness.ts` passes the clearance
   * that keeps a body from taking the press meant for the board.
   *
   * **It moves nobody named, and it never will.** Since item 6 the only bodies
   * this class can hold are the unnamed crowd (see the module note), so calling
   * this is the client re-anchoring its own presentation. The simulation's
   * people turning out for news is a *sim* behaviour and is filed as the
   * conversation area's engagement work (item 6, rider 3) — a client that asked
   * the world to move somebody would be deciding something the world owns.
   */
  gatherAt(x: number, y: number, radius: number): void {
    this.focus = { x, y };

    for (const wanderer of this.wanderers) {
      const awayX = wanderer.homeX - x;
      const awayY = wanderer.homeY - y;
      const distance = Math.hypot(awayX, awayY);

      if (distance < 1e-3) {
        // Standing on the focus already: any bearing will do, and the figure's
        // own facing is the one that makes them turn least.
        wanderer.anchorX = x - Math.sin(wanderer.facing) * radius;
        wanderer.anchorY = y + Math.cos(wanderer.facing) * radius;
      } else {
        wanderer.anchorX = x + (awayX / distance) * radius;
        wanderer.anchorY = y + (awayY / distance) * radius;
      }

      // Walk now rather than at the end of whatever dwell was running: a crowd
      // that reacts to a town's worst morning seven seconds late is a crowd that
      // did not react to it.
      wanderer.dwell = 0;
      wanderer.targetX = wanderer.anchorX;
      wanderer.targetY = wanderer.anchorY;
    }
  }

  /**
   * Says whether the town this crowd stands in is in ruin, and quietens it if
   * it is.
   *
   * Presentation over a projection: `WitnessSource.ruins()` says a town is
   * still in the wreckage of the last army through it, and quietening the
   * street is one of the things the client does about it — the other being the
   * wreckage props themselves (`witness/witness.ts`). Set when the crowd is
   * anchored and never per frame; a body already mid-walk finishes it and slows
   * down at the next dwell, which is what a change of mood looks like rather
   * than a snap.
   *
   * **Uncalled since item 6** — see {@link AmbientCrowd.subdued}.
   */
  setSubdued(subdued: boolean): void {
    this.subdued = subdued;
  }

  /** Whether this crowd is quietened. For the self-checks. */
  get quiet(): boolean {
    return this.subdued;
  }

  /** Sends everybody back to the spot they were loitering at before. */
  release(): void {
    this.focus = null;

    for (const wanderer of this.wanderers) {
      wanderer.anchorX = wanderer.homeX;
      wanderer.anchorY = wanderer.homeY;
      wanderer.dwell = 0;
      wanderer.targetX = wanderer.homeX;
      wanderer.targetY = wanderer.homeY;
    }
  }

  /** Advances every body by one fixed step. */
  step(stepSeconds: number): void {
    for (const wanderer of this.wanderers) {
      this.stepOne(wanderer, stepSeconds);
    }
  }

  private stepOne(wanderer: Wanderer, stepSeconds: number): void {
    if (wanderer.dwell > 0) {
      wanderer.dwell -= stepSeconds;
      wanderer.walking = false;

      // Standing at a gathering means standing *facing* it. This is the one
      // thing the ambient loop does that is not wandering, and it is what turns
      // a ring of bodies into an audience.
      if (this.focus !== null) {
        turnTowards(
          wanderer,
          Math.atan2(-(this.focus.x - wanderer.x), this.focus.y - wanderer.y),
          stepSeconds,
        );
      }

      if (wanderer.dwell <= 0) {
        this.chooseTarget(wanderer);
      }

      return;
    }

    const toX = wanderer.targetX - wanderer.x;
    const toY = wanderer.targetY - wanderer.y;
    const distance = Math.hypot(toX, toY);

    if (distance <= ARRIVED_TILES) {
      // Arrived: stand about for a while before deciding to be somewhere else.
      wanderer.walking = false;
      wanderer.dwell = dwellSeconds(wanderer.random) * (this.subdued ? SUBDUED_DWELL_FACTOR : 1);

      return;
    }

    wanderer.walking = true;

    // Never overshoot: a step longer than what is left of the walk lands exactly
    // on the target, which is what keeps a body from oscillating across it at
    // low frame rates.
    const step = Math.min(AMBLE_SPEED * stepSeconds, distance);
    wanderer.x += (toX / distance) * step;
    wanderer.y += (toY / distance) * step;

    // Yaw 0 faces +y and positive yaw turns left, so the heading of a movement
    // vector is `atan2(-dx, dy)` — the inverse of `controller.ts`'s
    // `(-sin, cos)`.
    turnTowards(wanderer, Math.atan2(-toX, toY), stepSeconds);
    this.settle(wanderer);
  }

  /** Picks somewhere inside the loiter circle to amble to. */
  private chooseTarget(wanderer: Wanderer): void {
    // Uniform over the disc rather than over `(radius, angle)`: the square root
    // is what stops every body from clustering at its own anchor, which is
    // visible with three bodies and a three-tile radius.
    const angle = wanderer.random() * Math.PI * 2;
    // A body at a gathering shuffles where it stands instead of wandering off,
    // which is both what an audience does and what keeps the ring the width its
    // caller sized it to be ({@link GATHERED_LOITER_TILES}).
    const spread =
      (this.focus === null ? LOITER_RADIUS_TILES : GATHERED_LOITER_TILES) *
      // A ruined town's people drift less as well as move less often. Applied
      // to the gathering radius too, and deliberately: a crowd that turns out
      // for news in a town that is still burning stands closer together.
      (this.subdued ? SUBDUED_LOITER_FACTOR : 1);
    const radius = Math.sqrt(wanderer.random()) * spread;

    wanderer.targetX = wanderer.anchorX + Math.cos(angle) * radius;
    wanderer.targetY = wanderer.anchorY + Math.sin(angle) * radius;
  }

  /**
   * Puts the body's feet back on the ground, at most once per tile crossed.
   *
   * The probe is a handful of terrain queries and those cross the wasm boundary,
   * so it is run when the body enters a new tile rather than on every step: an
   * ambling body changes tile about once a second and the ground under it does
   * not move in between.
   */
  private settle(wanderer: Wanderer): void {
    const tileX = Math.floor(wanderer.x);
    const tileY = Math.floor(wanderer.y);

    if (tileX === wanderer.probedX && tileY === wanderer.probedY) {
      return;
    }

    wanderer.probedX = tileX;
    wanderer.probedY = tileY;
    wanderer.z = surfaceZ(this.terrain, wanderer.x, wanderer.y);
  }
}

/**
 * Returns the height a body's feet rest at over a point of the world.
 *
 * It starts from `TerrainSampler.groundLevel`, which re-derives the elevation
 * field from the seed and therefore answers **before any terrain exists** — the
 * property `world/wasm.ts` has always used to place the player's spawn, and the
 * one that lets a settlement be populated on the first frame instead of after
 * the streaming window has caught up. Then it corrects that answer against the
 * tiles actually there:
 *
 * - a **ramp** answers with its slope height, because a ramp is not solid
 *   (`player/slope.ts`) and a scan that only asked `isSolid` would walk straight
 *   past one and put the body a level too low, inside the hill it is standing
 *   on;
 * - a **solid** tile answers with its top face, which is what picks up a terrace
 *   the elevation field rounded and a pit somebody dug;
 * - **an ungenerated tile is skipped**, and a column with nothing generated in
 *   it therefore falls back to the elevation field's own answer;
 * - **a tree and a house wall are skipped too**
 *   ({@link TerrainSampler.standsOnGround}, wave 1.5): both are things standing
 *   *on* the ground rather than the ground, so a scan that stopped at one put a
 *   body on top of the tree, or a market board on a rooftop.
 *
 * That last clause is the whole reason {@link TerrainSampler.isGenerated}
 * exists, and it was a bug before it did: `isSolid` unions "the world has not
 * generated this" into "solid" so that a falling player is held up at the
 * streaming frontier, which means a scan run before the first tick meets rock at
 * the very first level it probes and puts the body — or a grave, or the market
 * board — two tiles into the sky. Everything the client places is placed before
 * the first tick, so that was every prop in the settlement.
 *
 * Always answers, so no caller has to have an opinion about a body with nowhere
 * to stand.
 */
export function surfaceZ(terrain: TerrainSampler, x: number, y: number): number {
  const tileX = Math.floor(x);
  const tileY = Math.floor(y);
  // The elevation field's answer *is* the standing level — worldgen puts the
  // floor tile there and the rock below it (`world/types.ts`), so nothing is
  // added to it here.
  const nominal = terrain.groundLevel(tileX, tileY);

  for (let z = nominal + PROBE_UP; z >= nominal - PROBE_DOWN; z -= 1) {
    // The shape query needs no generation guard: an ungenerated tile has no
    // shape, and no shape is neither a ramp nor a flight (`world/types.ts`).
    //
    // **A flight is walked as a ramp, and that is an approximation with a
    // stated size.** Since wave 1.5 a town's own ground climbs in steps rather
    // than in slopes ({@link TILE_SHAPE_STAIR_UP}), and the simulation answers
    // for both through the same rise query — one rule, because a ramp and a
    // stair are the same question about which way the ground goes up
    // (`sim_wasm`'s `ramp_rise`). What differs is the *surface*: a flight's is a
    // staircase and this reads it as the straight line touching the **outer
    // corners** of its treads. So a foot is level with the tread at each of
    // those corners and below it everywhere else, by at most **a whole step —
    // half a tile** — which is where it stands at the tile's low edge and again
    // immediately past the riser in its middle. `player/slope.ts` states the
    // same bound and `dev/selfcheck/player.ts` checks it. The alternative is
    // the client keeping its own copy of how many micro-steps the mesher draws
    // — a number with two homes, which drifts.
    const shape = terrain.tileShape(tileX, tileY, z);

    if (shape === TILE_SHAPE_RAMP || shape === TILE_SHAPE_STAIR_UP) {
      return rampSurfaceZ({ x: tileX, y: tileY, z }, terrain.rampRise(tileX, tileY, z), x, y);
    }

    if (!terrain.isSolid(tileX, tileY, z)) {
      continue;
    }

    // Generation is asked second and only about a tile that already claimed to
    // be solid, so the extra call across the boundary is paid once per probe
    // rather than once per level.
    if (!terrain.isGenerated(tileX, tileY, z)) {
      continue;
    }

    // And the footing question is asked third, on exactly the same terms and
    // for exactly the same reason: a trunk and a house wall are solid,
    // generated, and not the ground. Wave 1.5 opened this door for this
    // function by name (`sim_core::worldgen::stands_on_ground_at`, whose own
    // documentation cites the screenshot of a driver standing on a rooftop) and
    // then wired nothing to it — the paragraph above described a guard that was
    // not here. This is the guard.
    if (terrain.standsOnGround(tileX, tileY, z)) {
      continue;
    }

    return z + 1;
  }

  return nominal;
}

/** Turns a body towards a heading at {@link TURN_RATE}, the short way round. */
function turnTowards(wanderer: Wanderer, heading: number, stepSeconds: number): void {
  let delta = heading - wanderer.facing;

  // Into -PI..PI, so a body turning from +179 to -179 degrees turns two degrees
  // rather than three hundred and fifty-eight.
  while (delta > Math.PI) {
    delta -= Math.PI * 2;
  }

  while (delta < -Math.PI) {
    delta += Math.PI * 2;
  }

  const limit = TURN_RATE * stepSeconds;
  wanderer.facing += Math.min(limit, Math.max(-limit, delta));
}

/** How long to stand still this time. */
function dwellSeconds(random: () => number): number {
  return DWELL_SECONDS_MIN + random() * (DWELL_SECONDS_MAX - DWELL_SECONDS_MIN);
}

/**
 * A tiny seeded generator, so the crowd mills about the same way twice.
 *
 * Mulberry32 — thirty-two bits of state, no dependency, and good enough for
 * deciding where somebody stands. **It is not the simulation's rng and must
 * never become an input to one**: the sim's determinism charter owns
 * `Domain`-split streams, and this is presentation state that is not hashed and
 * not recorded (plan decision 8).
 */
function seededRandom(seed: number): () => number {
  let state = (seed | 0) + 0x6d2b79f5;

  return (): number => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;

    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}
